import type {
  NeverPublishedProcessingActivitySaveInput,
  ProcessingActivityDetail,
  ProcessingActivityPurposeWriteInput,
  ProcessingActivityWriteDTO,
  SharedDraftOutput,
  SharedDraftWriteDTO,
} from 'config/api/processingActivities/processingActivities.types';

import type {
  ProcessingActivityCategoryPayloadDTO,
  ProcessingActivityEditorCategory,
  ProcessingActivityEditorFormValues,
  ProcessingActivityEditorPurpose,
  ProcessingPurposePayloadDTO,
} from './processingActivityEditor.types';

const createClientId = (): string => `purpose-${Math.random().toString(36).slice(2, 11)}`;

const parseJsonArray = <T>(value: unknown): T[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const serializeJsonArray = <T>(value: T[]): string => JSON.stringify(value);

export const isLegitimateInterestsBasis = (legalBasis: string): boolean => legalBasis === 'gdpr_6_1_f';

const isRetentionUnit = (value: unknown): value is ProcessingActivityEditorPurpose['retentionUnit'] =>
  value === 'days' || value === 'months' || value === 'years';

export const formatEditorPurposeRetentionPeriod = (
  purpose: Pick<ProcessingActivityEditorPurpose, 'retentionTrigger' | 'retentionValue' | 'retentionUnit' | 'retentionComment'>,
): string => {
  const periodPart = purpose.retentionValue != null && purpose.retentionUnit ? `${purpose.retentionValue} ${purpose.retentionUnit}` : '';
  const triggerPart = purpose.retentionTrigger.trim();

  if (periodPart && triggerPart) {
    return `${periodPart} after ${triggerPart}`;
  }
  if (periodPart) {
    return periodPart;
  }
  if (triggerPart) {
    return triggerPart;
  }

  return purpose.retentionComment.trim();
};

export const mapPurposeDtoToEditor = (dto: ProcessingPurposePayloadDTO, index: number): ProcessingActivityEditorPurpose => ({
  clientId: dto.id != null ? `purpose-${dto.id}` : createClientId(),
  id: dto.id,
  purposeText: dto.purpose_text ?? '',
  legalBasis: (dto.legal_basis as ProcessingActivityEditorPurpose['legalBasis']) ?? '',
  legalBasisComment: dto.legal_basis_comment ?? '',
  retentionTrigger: dto.retention_trigger ?? '',
  retentionValue: typeof dto.retention_value === 'number' ? dto.retention_value : null,
  retentionUnit: isRetentionUnit(dto.retention_unit) ? dto.retention_unit : '',
  retentionComment: dto.retention_comment ?? dto.retention_description ?? '',
  sortOrder: dto.sort_order ?? index,
  isSoftDeleted: Boolean(dto.is_deleted),
});

export const mapCategoryDtoToEditor = (dto: ProcessingActivityCategoryPayloadDTO): ProcessingActivityEditorCategory => ({
  id: dto.id,
  name: dto.name,
});

export const parseSharedDraftOutputToEditorForm = (draft: SharedDraftOutput): ProcessingActivityEditorFormValues => {
  const purposeDtos = parseJsonArray<ProcessingPurposePayloadDTO>(draft.purposes);

  return {
    name: draft.name,
    description: draft.description,
    processingRole: draft.processingRole,
    dataSubjectCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(draft.dataSubjectCategories).map(mapCategoryDtoToEditor),
    personalDataCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(draft.personalDataCategories).map(mapCategoryDtoToEditor),
    recipientCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(draft.recipientCategories).map(mapCategoryDtoToEditor),
    minimumSecurityMeasures: draft.minimumSecurityMeasures,
    purposes: purposeDtos.map(mapPurposeDtoToEditor),
  };
};

type ProcessingActivityEditorSource = Pick<
  ProcessingActivityDetail,
  'name' | 'description' | 'processingRole' | 'minimumSecurityMeasures'
> & {
  purposes?: string | ProcessingPurposePayloadDTO[];
  dataSubjectCategories?: string | ProcessingActivityCategoryPayloadDTO[];
  personalDataCategories?: string | ProcessingActivityCategoryPayloadDTO[];
  recipientCategories?: string | ProcessingActivityCategoryPayloadDTO[];
};

export const parseProcessingActivityDetailToEditorForm = (detail: ProcessingActivityEditorSource): ProcessingActivityEditorFormValues => {
  const purposeDtos = parseJsonArray<ProcessingPurposePayloadDTO>(detail.purposes);

  return {
    name: detail.name,
    description: detail.description,
    processingRole: detail.processingRole,
    dataSubjectCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.dataSubjectCategories).map(mapCategoryDtoToEditor),
    personalDataCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.personalDataCategories).map(mapCategoryDtoToEditor),
    recipientCategories: parseJsonArray<ProcessingActivityCategoryPayloadDTO>(detail.recipientCategories).map(mapCategoryDtoToEditor),
    minimumSecurityMeasures: detail.minimumSecurityMeasures,
    purposes: purposeDtos.map(mapPurposeDtoToEditor),
  };
};

export const mapEditorPurposeToDto = (purpose: ProcessingActivityEditorPurpose, index: number): ProcessingPurposePayloadDTO => ({
  ...(purpose.id != null ? { id: purpose.id } : {}),
  purpose_text: purpose.purposeText.trim(),
  legal_basis: purpose.legalBasis,
  ...(purpose.legalBasisComment.trim() ? { legal_basis_comment: purpose.legalBasisComment.trim() } : {}),
  lia_required: isLegitimateInterestsBasis(purpose.legalBasis),
  ...(purpose.retentionTrigger.trim() ? { retention_trigger: purpose.retentionTrigger.trim() } : {}),
  ...(purpose.retentionValue != null ? { retention_value: purpose.retentionValue } : {}),
  ...(purpose.retentionUnit ? { retention_unit: purpose.retentionUnit } : {}),
  ...(purpose.retentionComment.trim() ? { retention_comment: purpose.retentionComment.trim() } : {}),
  sort_order: index,
  is_deleted: purpose.isSoftDeleted,
});

export const mapEditorCategoryToDto = (category: ProcessingActivityEditorCategory): ProcessingActivityCategoryPayloadDTO => ({
  ...(category.id != null ? { id: category.id } : {}),
  name: category.name.trim(),
});

export const parseProcessingActivityEditorFormForBE = (values: ProcessingActivityEditorFormValues): ProcessingActivityWriteDTO => {
  const payload: ProcessingActivityWriteDTO = {
    name: values.name.trim(),
    description: values.description.trim(),
    minimum_security_measures: values.minimumSecurityMeasures.trim(),
    data_subject_categories: serializeJsonArray(values.dataSubjectCategories.map(mapEditorCategoryToDto)),
    personal_data_categories: serializeJsonArray(values.personalDataCategories.map(mapEditorCategoryToDto)),
    recipient_categories: serializeJsonArray(values.recipientCategories.map(mapEditorCategoryToDto)),
    purposes: serializeJsonArray(values.purposes.map(mapEditorPurposeToDto)),
  };

  if (values.processingRole) {
    payload.processing_role = values.processingRole;
  }

  return payload;
};

export const parseProcessingActivityEditorFormForDraftBE = (
  values: ProcessingActivityEditorFormValues,
  options?: { draftRevision?: number; changeComment?: string },
): SharedDraftWriteDTO => ({
  ...parseProcessingActivityEditorFormForBE(values),
  ...(options?.draftRevision != null ? { draft_revision: options.draftRevision } : {}),
  ...(options?.changeComment?.trim() ? { change_comment: options.changeComment.trim() } : {}),
});

const mapEditorPurposeToSaveInput = (purpose: ProcessingActivityEditorPurpose, index: number): ProcessingActivityPurposeWriteInput => ({
  ...(purpose.id != null ? { id: purpose.id } : {}),
  purposeText: purpose.purposeText.trim(),
  ...(purpose.legalBasis ? { legalBasis: purpose.legalBasis } : {}),
  ...(purpose.legalBasisComment.trim() ? { legalBasisComment: purpose.legalBasisComment.trim() } : {}),
  ...(purpose.retentionTrigger.trim() ? { retentionTrigger: purpose.retentionTrigger.trim() } : {}),
  ...(purpose.retentionValue != null ? { retentionValue: purpose.retentionValue } : {}),
  ...(purpose.retentionUnit ? { retentionUnit: purpose.retentionUnit } : {}),
  ...(purpose.retentionComment.trim() ? { retentionComment: purpose.retentionComment.trim() } : {}),
  sortOrder: index,
  ...(purpose.isSoftDeleted ? { isRemoved: true } : {}),
});

export const parseProcessingActivityEditorFormForNeverPublishedSave = (
  values: ProcessingActivityEditorFormValues,
): NeverPublishedProcessingActivitySaveInput => ({
  name: values.name.trim(),
  description: values.description.trim(),
  ...(values.processingRole ? { processingRole: values.processingRole } : {}),
  minimumSecurityMeasures: values.minimumSecurityMeasures.trim(),
  purposes: values.purposes.map(mapEditorPurposeToSaveInput),
  dataSubjectCategories: values.dataSubjectCategories.map(category =>
    category.id != null ? { id: category.id } : { name: category.name.trim() },
  ),
  personalDataCategories: values.personalDataCategories.map(category =>
    category.id != null ? { id: category.id } : { name: category.name.trim() },
  ),
  recipientCategories: values.recipientCategories.map(category =>
    category.id != null ? { id: category.id } : { name: category.name.trim() },
  ),
});
